import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, AlertCircle, CheckCircle2, MessageSquare, Trash2, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

const iconForKind = (k) => {
  if (k === "incident") return <AlertCircle className="h-5 w-5 text-red-600" />;
  if (k === "done") return <CheckCircle2 className="h-5 w-5 text-[#4d7c0f]" />;
  if (k === "reply") return <MessageSquare className="h-5 w-5 text-[#7c3aed]" />;
  if (k === "payment_paid") return <CheckCircle2 className="h-5 w-5 text-emerald-600" />;
  return <Bell className="h-5 w-5 text-slate-500" />;
};

export default function NotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/notifications/mine");
      setItems(data || []);
    } catch (_) {}
  }, [user]);

  useEffect(() => {
    load();
    if (!user) return;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load, user]);

  if (!user) return null;

  const unread = items.filter((n) => !n.read).length;

  const onOpenChange = (v) => {
    setOpen(v);
  };

  const openNotification = async (n) => {
    if (!n.code) return;
    try { await api.post(`/notifications/${n.id}/read`); } catch (_) {}
    setItems((prev) => prev.map((item) => item.id === n.id ? { ...item, read: true } : item));
    setOpen(false);
    navigate(`/codes/${encodeURIComponent(n.code)}`);
    setTimeout(load, 300);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-bell"
          className="relative rounded-full p-2 transition-colors hover:bg-slate-100"
          aria-label="Notificaciones"
        >
          <Bell className={`h-6 w-6 ${unread > 0 ? "text-[#4d7c0f]" : "text-slate-600"}`} strokeWidth={2.5} />
          {unread > 0 && (
            <span
              data-testid="notifications-badge"
              className="absolute -right-0.5 -top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-[11px] font-black text-white ring-2 ring-white"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <span className="text-sm font-extrabold text-slate-900">Notificaciones</span>
          <div className="flex items-center gap-1">
            {items.length > 0 && unread > 0 && (
              <Button
                data-testid="notifications-mark-all"
                size="sm"
                variant="ghost"
                onClick={async () => {
                  try { await api.post("/notifications/read-all"); } catch (_) {}
                  load();
                }}
                className="h-7 px-2 text-xs"
              >
                <Check className="mr-1 h-3 w-3" /> Leídas
              </Button>
            )}
            {items.length > 0 && (
              <Button
                data-testid="notifications-delete-all"
                size="sm"
                variant="ghost"
                onClick={async () => {
                  if (!window.confirm("¿Borrar todas las notificaciones?")) return;
                  try { await api.delete("/notifications"); } catch (_) {}
                  setItems([]);
                }}
                className="h-7 px-2 text-xs text-red-700 hover:bg-red-50"
              >
                <Trash2 className="mr-1 h-3 w-3" /> Borrar
              </Button>
            )}
          </div>
        </div>
        <ul className="max-h-96 divide-y divide-slate-100 overflow-y-auto">
          {items.length === 0 && (
            <li className="p-6 text-center text-sm text-slate-500">Sin notificaciones</li>
          )}
          {items.map((n) => {
            const canOpenOrder = Boolean(n.code);
            return (
            <li
              key={n.id}
              data-testid={`notif-${n.id}`}
              className={`flex gap-3 p-3 ${n.read ? "" : "bg-[#4d7c0f]/5"} ${canOpenOrder ? "hover:bg-slate-50" : ""}`}
            >
              <div
                onClick={() => openNotification(n)}
                className={`mt-0.5 shrink-0 ${canOpenOrder ? "cursor-pointer" : ""}`}
              >
                {iconForKind(n.kind)}
              </div>
              <div
                onClick={() => openNotification(n)}
                className={`min-w-0 flex-1 ${canOpenOrder ? "cursor-pointer" : ""}`}
              >
                <div className="text-sm font-bold text-slate-900">{n.title}</div>
                <p className="mt-0.5 whitespace-pre-wrap text-xs text-slate-700">{n.body}</p>
                <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-400">
                  {new Date(n.created_at).toLocaleString("es-ES")}
                </div>
              </div>
              <button
                type="button"
                aria-label="Borrar notificación"
                data-testid={`delete-notif-${n.id}`}
                onClick={async (e) => {
                  e.stopPropagation();
                  try { await api.delete(`/notifications/${n.id}`); } catch (_) {}
                  setItems((prev) => prev.filter((item) => item.id !== n.id));
                }}
                className="mt-0.5 rounded-full p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
              >
                <X className="h-4 w-4" />
              </button>
            </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
